import { getTimeZones } from '@vvo/tzdb'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import objectSupport from 'dayjs/plugin/objectSupport'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { format } from 'date-fns'

import { __REACT_NATIVE__ } from '@disco/constants/environment'

dayjs.extend(objectSupport)
dayjs.extend(advancedFormat)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrBefore)
dayjs.extend(isToday)
dayjs.extend(duration)
dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)

const SECOND_MS = 1000
const MINUTE_MS = SECOND_MS * 60
const HOUR_MS = MINUTE_MS * 60
const DAY_MS = HOUR_MS * 24

/**
 * Formats a time duration in seconds to a human-readable time string.
 *
 * Automatically chooses between mm:ss and HH:mm:ss format based on duration.
 * Optionally allows single-digit formatting for hours/minutes.
 *
 * @param time - Duration in seconds
 * @param singleDigits - Whether to use single digits for hours/minutes (default: false)
 * @returns Formatted time string
 *
 * @example
 * ```typescript
 * formatTime(90) // returns '01:30'
 * formatTime(3661) // returns '01:01:01'
 * formatTime(90, true) // returns '1:30'
 * formatTime(3661, true) // returns '1:01:01'
 * ```
 */
export const formatTime = (
  time: number,
  singleDigits: boolean = false,
): string => {
  let formatString = time < 3600 ? 'mm:ss' : 'HH:mm:ss'
  if (singleDigits) formatString = time < 3600 ? 'm:ss' : 'H:mm:ss'

  return formatDuration(time, formatString)
}

/**
 * Formats a duration using a custom format string.
 *
 * Properly handles durations longer than 24 hours, unlike dayjs UTC which wraps around.
 * Supports common format strings like 'HH:mm:ss', 'H:mm:ss', 'mm:ss', 'm:ss'.
 *
 * @param duration - Duration in seconds
 * @param formatString - Format string (default: 'HH:mm:ss')
 * @returns Formatted duration string
 *
 * @example
 * ```typescript
 * formatDuration(3661) // returns '01:01:01'
 * formatDuration(3661, 'H:mm:ss') // returns '1:01:01'
 * formatDuration(90, 'mm:ss') // returns '01:30'
 * formatDuration(86400) // returns '24:00:00' (24 hours)
 * ```
 */
export const formatDuration = (
  duration: number,
  formatString: string = 'HH:mm:ss',
): string => {
  const hours = Math.floor(duration / 3600)
  const minutes = Math.floor((duration % 3600) / 60)
  const seconds = Math.floor(duration % 60)

  // Replace format tokens with actual values
  return formatString
    .replace(/HH/g, hours.toString().padStart(2, '0'))
    .replace(/H/g, hours.toString())
    .replace(/mm/g, minutes.toString().padStart(2, '0'))
    .replace(/m/g, minutes.toString())
    .replace(/ss/g, seconds.toString().padStart(2, '0'))
    .replace(/s/g, seconds.toString())
}

/**
 * Formats a duration in seconds to a human-readable string like "21:08".
 *
 * @param totalSeconds - Duration in seconds
 * @returns Human-readable duration string in HH:MM format
 *
 * @example
 * ```typescript
 * formatHumanReadableDuration(5400) // returns '1:30hs'
 * formatHumanReadableDuration(2700) // returns '0:45hs'
 * formatHumanReadableDuration(76080) // returns '21:08hs'
 * ```
 */
export const formatHumanReadableDuration = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const paddedMinutes = minutes.toString().padStart(2, '0')
  return `${hours}:${paddedMinutes}`
}

/**
 * Formats a timestamp to a full date and time string.
 *
 * @param timestamp - The timestamp to format (Date, string, or number)
 * @returns Formatted string like "January 1st 2024, 3:30pm", or empty string if invalid
 *
 * @example
 * ```typescript
 * formatTimestamp(new Date('2024-01-01 15:30')) // returns 'January 1st 2024, 3:30pm'
 * formatTimestamp('') // returns ''
 * ```
 */
export const formatTimestamp = (timestamp: any): string => {
  if (!timestamp) return ''
  return dayjs(timestamp).format('MMMM Do YYYY, h:mma')
}

/**
 * Formats a timestamp to a full date string without time.
 *
 * @param timestamp - The timestamp to format (Date, string, or number)
 * @returns Formatted string like "January 1, 2024", or empty string if invalid
 *
 * @example
 * ```typescript
 * formatDate(new Date('2024-01-01')) // returns 'January 1, 2024'
 * formatDate('') // returns ''
 * ```
 */
export const formatDate = (timestamp: any): string => {
  if (!timestamp) return ''
  return dayjs(timestamp).format('MMMM D, YYYY')
}

/**
 * Formats a timestamp to a short date string.
 *
 * @param timestamp - The timestamp to format (Date, string, or number)
 * @returns Formatted string like "Jan 1, 2024", or empty string if invalid
 *
 * @example
 * ```typescript
 * formatDateShort(new Date('2024-01-01')) // returns 'Jan 1, 2024'
 * formatDateShort('') // returns ''
 * ```
 */
export const formatDateShort = (timestamp: string | number | Date): string => {
  if (!timestamp) return ''
  return dayjs(timestamp).format('MMM D, YYYY')
}

/**
 * Formats a timestamp as a relative time string (e.g., "2 hours ago").
 *
 * @param timestamp - The timestamp to format (Date, string, or number)
 * @returns Relative time string, or empty string if invalid
 *
 * @example
 * ```typescript
 * formatRelativeDate(new Date(Date.now() - 3600000)) // returns '1 hour ago'
 * formatRelativeDate('') // returns ''
 * ```
 */
export const formatRelativeDate = (timestamp: any): string => {
  if (!timestamp) return ''
  return dayjs(timestamp).fromNow()
}

/**
 * Formats a timestamp to a context-aware short format.
 *
 * Returns different formats based on the relationship to the current date:
 * - Same day: Shows time only (e.g., "3:30 PM")
 * - Same year: Shows month and day (e.g., "Jan 15")
 * - Different year: Shows month, day, and year (e.g., "Jan 15, 2023")
 *
 * @param date - The date string or Date to format
 * @returns Context-appropriate short format string
 *
 * @example
 * ```typescript
 * // Assuming today is Jan 15, 2024
 * formatTimestampShort('2024-01-15 15:30') // returns '3:30 PM'
 * formatTimestampShort('2024-01-10 15:30') // returns 'Jan 10'
 * formatTimestampShort('2023-01-15 15:30') // returns 'Jan 15, 2023'
 * ```
 */
export function formatTimestampShort(date: string | Date): string {
  const d = dayjs(date)
  if (d.isSame(dayjs(), 'day')) {
    return d.format('h:mm A')
  } else if (d.isSame(dayjs(), 'year')) {
    return d.format('MMM D')
  } else {
    return d.format('MMM D, YYYY')
  }
}

/**
 * Formats a timestamp for mobile display with a compact format.
 *
 * @param timestamp - The timestamp to format
 * @returns Formatted string like "15 Jan, 2024"
 *
 * @example
 * ```typescript
 * formatTimestampForMobile('2024-01-15') // returns '15 Jan, 2024'
 * ```
 */
export const formatTimestampForMobile = (timestamp: string): string =>
  dayjs(timestamp).format('D MMM, YYYY')

/**
 * Rounds a date to the nearest time increment.
 *
 * Rounds up to the next time increment boundary, ensuring that times
 * are aligned to specific intervals (e.g., 15, 30, 60 minutes).
 * Always rounds forward to avoid selecting times that have already passed.
 *
 * @param date - The date to round
 * @param timeIncrement - The increment in minutes to round to
 * @returns New Date object rounded to the nearest increment
 *
 * @example
 * ```typescript
 * const date = new Date('2024-01-01 13:07:30')
 * roundToNearestTimeIncrement(date, 30) // returns Date for '2024-01-01 13:30:00'
 * roundToNearestTimeIncrement(date, 15) // returns Date for '2024-01-01 13:15:00'
 * ```
 */
export const roundToNearestTimeIncrement = (
  date: Date,
  timeIncrement: number,
): Date => {
  const minutes = dayjs(date).get('minute')
  const remainder = minutes % timeIncrement
  // Ensure time option is not seconds away from the next time increment ie. 13:00:11 should round to 13:30 as time has passed
  const minutesToAdd = remainder >= 0 ? timeIncrement - remainder : 0
  const roundedTime = dayjs(date)
    .add(minutesToAdd, 'minute')
    .set('second', 0)
    .format('YYYY-MM-DD H:mm:ss')
  return new Date(roundedTime)
}

const roundTimetoDisplay = (
  timeIncrement: number,
  selectedDate: Date,
  timeZone: string,
  timeInAdvance?: number,
): Date => {
  let startTimeToDisplay
  // earliest available time starts from today (now) in timeZone
  let todayInTimeZone = dayjs().tz(timeZone)
  // Add time in advance to the selected date if specified in props
  if (timeInAdvance) {
    todayInTimeZone = todayInTimeZone.add(timeInAdvance, 'minute')
  } else if (dayjs(selectedDate)) {
    todayInTimeZone = dayjs(selectedDate).add(timeInAdvance ?? 0, 'minute')
  }

  const startTime = todayInTimeZone.format('HH:mm')

  const [startHours, startMinutes] = startTime.split(':').map(Number)
  const startOfBusinessDay = dayjs(selectedDate)
    .startOf('day')
    .add(startHours ?? 0, 'hour')
    .add(startMinutes ?? 0, 'minute')

  const roundedTime = roundToNearestTimeIncrement(
    todayInTimeZone.toDate(),
    timeIncrement,
  )
  startTimeToDisplay = roundedTime
  if (roundedTime.getTime() < startOfBusinessDay.toDate().getTime()) {
    startTimeToDisplay = roundToNearestTimeIncrement(
      startOfBusinessDay.toDate(),
      timeIncrement,
    )
  }
  return startTimeToDisplay
}

/**
 * Recursively generates time selection options for a given time range.
 *
 * Creates an array of time options from a start time to end of day,
 * with specified time increments.
 *
 * @param time - The current time being processed (dayjs object)
 * @param endOfTheDay - The end time limit (dayjs object)
 * @param timeIncrement - The increment in minutes between options
 * @param times - Accumulator array for time options (default: [])
 * @returns Array of time options with value (HH:mm) and label (h:mm A)
 *
 * @example
 * ```typescript
 * const startTime = dayjs().startOf('day').hour(9)
 * const endTime = dayjs().endOf('day')
 * const options = formatTimeSelection(startTime, endTime, 30)
 * // [{ value: '09:00', label: '9:00 AM' }, { value: '09:30', label: '9:30 AM' }, ...]
 * ```
 */
function formatTimeSelection(
  time: any,
  endOfTheDay: any,
  timeIncrement: number,
  times: TimeOption[] = [],
): TimeOption[] {
  if (time > endOfTheDay) {
    return times
  }
  times.push({
    value: time.format('HH:mm'),
    label: time.format('h:mm A'),
  })
  return formatTimeSelection(
    time.add(timeIncrement, 'minute'),
    endOfTheDay,
    timeIncrement,
    times,
  )
}

export type TimeOption = {
  label: string
  value: string
}

/**
 * Rotates time options array to start from a specific time.
 *
 * Reorders the time options so that the specified start time appears first,
 * with subsequent times following in chronological order.
 *
 * @param formattedStartTime - The time to start the rotation from
 * @param timeOptions - Array of time options to rotate
 * @returns Reordered array starting from the specified time
 *
 * @example
 * ```typescript
 * const options = [
 *   { value: '09:00', label: '9:00 AM' },
 *   { value: '10:00', label: '10:00 AM' },
 *   { value: '11:00', label: '11:00 AM' }
 * ]
 * const startTime = new Date('2024-01-01 10:00')
 * const rotated = rotateTimeOptionsToStart(startTime, options)
 * // Starts from 10:00 AM option
 * ```
 */
const rotateTimeOptionsToStart = (
  formattedStartTime: Date,
  timeOptions: TimeOption[],
): TimeOption[] => {
  const startIndex = timeOptions.findIndex(
    time => time.value === dayjs(formattedStartTime).format('HH:mm'),
  )
  return [...timeOptions.slice(startIndex), ...timeOptions.slice(0, startIndex)]
}

/**
 * Formats a time string and date into a standardized time option.
 *
 * Converts a time string (HH:mm) and date into a time option object
 * with both 24-hour and 12-hour format representations.
 *
 * @param time - Time string in HH:mm format
 * @param date - Date string to apply the time to
 * @returns Time option object with value and label
 *
 * @example
 * ```typescript
 * const option = formatTimeForDisplay('14:30', '2024-01-01')
 * // { value: '14:30', label: '2:30 PM' }
 * ```
 */
export const formatTimeForDisplay = (
  time: string,
  date: string,
): TimeOption => {
  const [hours, minutes] = time.split(':')
  const timeToDisplay = dayjs(date)
    .set('hour', parseInt(hours ?? '0'))
    .set('minute', parseInt(minutes ?? '0'))

  return {
    value: timeToDisplay.format('HH:mm'),
    label: timeToDisplay.format('h:mm A'),
  }
}

/**
 * Generates time selection options for a specific date and timezone.
 *
 * Creates an array of time options starting from the appropriate time
 * (considering current time if today) with specified increments.
 *
 * @param date - The base date for time generation
 * @param timeIncrement - Minutes between each time option
 * @param selectedDate - The selected date to generate times for
 * @param timeZone - Timezone string for calculations
 * @param timeInAdvance - Optional minimum time advance in minutes
 * @returns Array of time options rotated to start from the appropriate time
 *
 * @example
 * ```typescript
 * const options = generateTimeOptions(
 *   new Date(),
 *   30,
 *   new Date('2024-01-01'),
 *   'America/New_York',
 *   60
 * )
 * // Returns 30-minute interval options starting from appropriate time
 * ```
 */
export const generateTimeOptions = (
  date: Date,
  timeIncrement: number,
  selectedDate: Date,
  timeZone: string,
  timeInAdvance?: number,
): TimeOption[] => {
  const formattedStartTime = roundTimetoDisplay(
    timeIncrement,
    selectedDate,
    timeZone,
    timeInAdvance,
  )

  const endOfTheDay = dayjs(selectedDate).endOf('day')
  const time = dayjs(date).isToday()
    ? dayjs(formattedStartTime)
    : dayjs(date).startOf('day')

  const timeOptions = formatTimeSelection(time, endOfTheDay, timeIncrement)
  // @ts-expect-error todo fix later
  return rotateTimeOptionsToStart(time, timeOptions)
}

/**
 * Generates a list of human-readable timezone options.
 *
 * Creates timezone options with GMT offset labels and grouping information.
 * Returns empty array for React Native environments.
 *
 * @returns Array of timezone objects with label, value, and group properties
 *
 * @example
 * ```typescript
 * const timezones = humanReadableTimeZones()
 * // [
 * //   { label: '(GMT-05:00) Eastern Time', value: 'America/New_York', group: [...] },
 * //   { label: '(GMT-08:00) Pacific Time', value: 'America/Los_Angeles', group: [...] }
 * // ]
 * ```
 */
export const humanReadableTimeZones = (): Array<{
  label: string
  value: string
  group: string[]
}> => {
  // TODO: React Native doesn't support timezone libraries - need to make own list of timezones
  const timeZones = __REACT_NATIVE__ ? [] : getTimeZones()
  return timeZones.map(timeZone => {
    const label = timeZone.currentTimeFormat.replace(
      /([+-]\d{2}:\d{2})/,
      '(GMT$1)',
    )
    return {
      label: label,
      value: timeZone.name,
      group: timeZone.group,
    }
  })
}

/**
 * Formats a timezone string into a human-readable option.
 *
 * Finds the corresponding human-readable timezone from the list
 * and returns it in option format.
 *
 * @param selectedTimeZone - The timezone string to format
 * @returns Timezone option object or null/undefined if not found
 *
 * @example
 * ```typescript
 * const option = formatHumanReadableTimezone('America/New_York')
 * // { value: 'America/New_York', label: '(GMT-05:00) Eastern Time' }
 * ```
 */
export const formatHumanReadableTimezone = (
  selectedTimeZone: string,
): any | null | undefined => {
  const timezoneList = humanReadableTimeZones()
  let selectedTimezone
  if (Array.isArray(timezoneList)) {
    selectedTimezone = timezoneList.find(
      timezone => timezone.value === selectedTimeZone,
    )
    return { value: selectedTimeZone, label: selectedTimezone?.label }
  }
}

/**
 * Automatically detects and formats the user's current timezone.
 *
 * Uses dayjs timezone guessing to determine the current timezone
 * and formats it into a human-readable option with GMT offset.
 *
 * @returns Timezone option object with current timezone information
 *
 * @example
 * ```typescript
 * const currentTz = guessCurrentTimezone()
 * // { label: '(GMT-05:00) America/New York', value: 'America/New_York' }
 * ```
 */
export const guessCurrentTimezone = (): any => {
  const timeZone = dayjs.tz.guess()

  const selectedDateInTimezone = dayjs.tz(new Date(), timeZone)
  const timeDifference = 'GMT' + selectedDateInTimezone.format('Z')

  const [region, city] = timeZone
    .split('/')
    .map(part => part.replace(/_/g, ' '))

  const humanReadableTimezone = humanReadableTimeZones().find(
    t => t.value === timeZone || t.group.includes(timeZone),
  )
  if (humanReadableTimezone) return humanReadableTimezone

  return {
    label: `(${timeDifference}) ${region}/${city}`,
    value: timeZone,
  }
}

/**
 * Sets the time portion of a date while preserving the date portion.
 *
 * Applies the current system time (hours, minutes, seconds) to a given date,
 * optionally converting to a specific timezone.
 *
 * @param date - The date to apply the time to
 * @param timeZone - Optional timezone string for conversion
 * @returns Date object with current time applied
 *
 * @example
 * ```typescript
 * const tomorrow = new Date('2024-01-02')
 * const withCurrentTime = getTimeOfDate(tomorrow, 'America/New_York')
 * // Tomorrow's date with current time in NY timezone
 * ```
 */
export const getTimeOfDate = (date: Date, timeZone?: string): Date => {
  const hours = dayjs().get('hour')
  const minutes = dayjs().get('minute')
  const seconds = dayjs().get('second')
  const timeCheck = dayjs(date)
    .set('hour', hours)
    .set('minute', minutes)
    .set('second', seconds)

  return timeZone ? timeCheck.tz(timeZone).toDate() : timeCheck.toDate()
}

/**
 * Sets a specific time on a given date.
 *
 * Applies a time string (HH:mm format) to a date,
 * optionally converting to a specific timezone.
 *
 * @param date - The date to apply the time to
 * @param time - Time string in HH:mm format
 * @param timeZone - Optional timezone string for conversion
 * @returns Date object with the specified time applied
 *
 * @example
 * ```typescript
 * const date = new Date('2024-01-01')
 * const withTime = setTimeOfDate(date, '14:30', 'Europe/London')
 * // January 1st, 2024 at 2:30 PM London time
 * ```
 */
export const setTimeOfDate = (
  date: Date,
  time: string,
  timeZone?: string,
): Date => {
  const [hours, minutes] = time?.split(':') || []
  const dateTime = dayjs(date)
    .set('hour', parseInt(hours ?? '0'))
    .set('minute', parseInt(minutes ?? '0'))
  return timeZone ? dateTime.tz(timeZone).toDate() : dateTime.toDate()
}

/**
 * Checks if a selected date is in the past relative to today in a specific timezone.
 *
 * Compares dates in YYYY-MM-DD format to determine if the selected date
 * has already passed in the specified timezone.
 *
 * @param selectedDate - Date string to check
 * @param selectedTimeZone - Timezone string for comparison
 * @returns True if the selected date is in the past
 *
 * @example
 * ```typescript
 * const isPast = checkDateIsInPast('2024-01-01', 'America/New_York')
 * // true if current date in NY timezone is after January 1st, 2024
 * ```
 */
export const checkDateIsInPast = (
  selectedDate: string,
  selectedTimeZone: string,
): boolean => {
  const selectedDateFormat = dayjs(selectedDate).format('YYYY-MM-DD')
  const todayInTimeZone = dayjs().tz(selectedTimeZone).format('YYYY-MM-DD')

  return dayjs(selectedDateFormat).isBefore(dayjs(todayInTimeZone))
}

/**
 * Checks if the first time is before the second time.
 *
 * Compares two time strings to determine chronological order.
 * Both times are formatted to YYYY-MM-DDTHH:mm:ss for comparison.
 *
 * @param firstTime - First time string to compare
 * @param secondTime - Second time string to compare
 * @returns True if the second time is after the first time
 *
 * @example
 * ```typescript
 * const isEarlier = checkTimeIsInPast('2024-01-01 10:00', '2024-01-01 11:00')
 * // true (11:00 is after 10:00)
 * ```
 */
export const checkTimeIsInPast = (
  firstTime: string,
  secondTime: string,
): boolean => {
  const formatFirstTime = dayjs(firstTime).format('YYYY-MM-DDTHH:mm:ss')
  const formatSecondTime = dayjs(secondTime).format('YYYY-MM-DDTHH:mm:ss')

  return dayjs(formatSecondTime).isAfter(formatFirstTime)
}

export type TimeRemainingProps = {
  date?: string // e.g. 2024-12-28,
  time?: string // e.g. 23:30,
  timeZone?: string // e.g. Europe/Rome
}
export type TimeRemaining =
  | {
      days: number
      hours: number
      minutes: number
      seconds: number
      difference: number
    }
  | undefined

/**
 * Calculates the time remaining until a specific date and time.
 *
 * Computes the difference between now and a target date/time,
 * returning the breakdown in days, hours, minutes, and seconds.
 *
 * @param props - Object containing date, time, and timezone information
 * @param props.date - Target date string (e.g., '2024-12-28')
 * @param props.time - Target time string (e.g., '23:30')
 * @param props.timeZone - Timezone string (e.g., 'Europe/Rome')
 * @returns Object with time breakdown or undefined if target has passed
 *
 * @example
 * ```typescript
 * const remaining = calculateTimeRemaining({
 *   date: '2024-12-31',
 *   time: '23:59',
 *   timeZone: 'America/New_York'
 * })
 * // { days: 5, hours: 10, minutes: 30, seconds: 45, difference: 458445000 }
 * ```
 */
export const calculateTimeRemaining = ({
  date,
  time,
  timeZone,
}: TimeRemainingProps): TimeRemaining => {
  if (!date || !time || !timeZone) return undefined

  const [selectedTimeHour, selectedTimeMinute] = time
    .split(':')
    .map(n => parseInt(n))
  const [selectedDateYear, selectedDateMonth, selectedDateDay] = date
    .split('-')
    .map(n => parseInt(n))

  // create date in selected time zone and set proper year/month/day/hour/minute
  const targetDate = dayjs
    .tz(dayjs(date), timeZone)
    .year(selectedDateYear ?? 0)
    // ".month()" accepts numbers from 0 to 11. If the range is exceeded, it will bubble up to the year.
    // Months are zero indexed, so January is month 0.
    .month((selectedDateMonth ?? 1) - 1)
    .date(selectedDateDay ?? 1)
    .hour(selectedTimeHour ?? 0)
    .minute(selectedTimeMinute ?? 0)

  // create current date in selected timezone
  const now = dayjs.tz(dayjs(), timeZone)

  const difference = targetDate.diff(now, 'ms', true)

  if (difference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      difference: 0,
    }
  }

  const days = Math.floor(difference / DAY_MS)
  const hours = Math.floor((difference % DAY_MS) / HOUR_MS)
  const minutes = Math.floor((difference % HOUR_MS) / MINUTE_MS)
  const seconds = Math.floor((difference % MINUTE_MS) / SECOND_MS)

  return {
    days,
    hours,
    minutes,
    seconds,
    difference,
  }
}

/**
 * Validates if a string represents a valid date.
 *
 * Uses dayjs to parse and validate the date string format.
 *
 * @param value - String to validate as a date
 * @returns True if the string represents a valid date
 *
 * @example
 * ```typescript
 * isValidDate('2024-01-01') // true
 * isValidDate('invalid-date') // false
 * isValidDate('2024-13-45') // false
 * ```
 */
export const isValidDate = (value: string): boolean => {
  if (!value) return false

  const formats = ['YYYY-MM-DD', 'MMMM D, YYYY']
  return formats.some(format => dayjs(value, format, true).isValid())
}

/**
 * Formats a duration range string into a human-readable format.
 *
 * Converts duration ranges (like "120-180") into formatted time strings
 * with appropriate units (seconds, minutes, hours).
 *
 * @param range - Duration range string (e.g., "120-180")
 * @returns Formatted duration range string
 *
 * @example
 * ```typescript
 * formatDurationRangeToString('120-180') // "2:00 - 3:00"
 * formatDurationRangeToString('30-60') // "0:30 - 1:00"
 * ```
 */
export const formatDurationRangeToString = (range: string) => {
  if (!range) return 'All duration'
  const [start, end] = range.split('-').map(Number)

  const formatTime = (seconds: number) => {
    const date = new Date(0)
    date.setSeconds(seconds)
    return format(date, 'm:ss')
  }

  const formattedStart = formatTime(start ?? 0)
  const formattedEnd = formatTime(end ?? 0)

  if (start && end === 0) return `From ${formattedStart} to end of duration`

  return `${formattedStart}-${formattedEnd}`
}

/**
 * Formats a date with timezone information for display.
 *
 * Converts a date string to a formatted representation in the specified timezone,
 * or local timezone if none provided.
 *
 * @param date - Date string to format
 * @param timeZone - Optional timezone string for conversion
 * @returns Formatted date string with timezone consideration
 *
 * @example
 * ```typescript
 * formatDateWithTimezone('2024-01-01', 'Europe/London')
 * // Formatted date in London timezone
 *
 * formatDateWithTimezone('2024-01-01')
 * // Formatted date in local timezone
 * ```
 */
export const formatDateWithTimezone = (
  date: string,
  timeZone?: string | undefined,
) => {
  return (
    new Date(date)
      .toLocaleString(undefined, {
        timeZone: timeZone ?? undefined,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .toUpperCase() + (timeZone ? ' ' + timeZone : '')
  )
}

/**
 * Formats a duration (in seconds) as a timecode string. Produces `M:SS` under
 * one hour and `H:MM:SS` at or above one hour. Non-finite or negative inputs
 * are clamped to `0:00`.
 */
export function formatSeconds(totalSeconds: number): string {
  const s = Number.isFinite(totalSeconds)
    ? Math.max(0, Math.floor(totalSeconds))
    : 0
  const hh = Math.floor(s / 3600)
  const mm = Math.floor((s % 3600) / 60)
  const ss = s % 60
  const pad = (n: number) => n.toString().padStart(2, '0')
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`
}
